import {
    Fragment,
    useCallback,
    useEffect,
    useId,
    useRef,
    useState,
} from 'react';
import { FiChevronDown, FiSearch } from 'react-icons/fi';
import { abreGrupo, filtrarOpcoes } from './filtrarOpcoes';
import styles from './SearchableSelect.module.css';

export interface SearchableSelectOption {
    value: string;
    label: string;
    prefix?: React.ReactNode;
    /**
     * Grupo da opcao. Opcoes do mesmo grupo devem vir juntas: a lista mostra o
     * nome do grupo como cabecalho e a busca tambem casa com ele (MAN-130).
     */
    group?: string;
}

interface SearchableSelectProps {
    options: SearchableSelectOption[];
    value: string;
    onChange: (value: string) => void;
    label?: string;
    placeholder?: string;
    emptyLabel?: string;
    error?: string;
    fullWidth?: boolean;
    disabled?: boolean;
    required?: boolean;
    className?: string;
    id?: string;
    dropdownVariant?: 'overlay' | 'inline';
    /** Texto do campo de busca. Padrao: "Buscar...". */
    searchPlaceholder?: string;
}

function useDebounce<T>(value: T, delay: number): T {
    const [debounced, setDebounced] = useState(value);

    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(timer);
    }, [value, delay]);

    return debounced;
}

export function SearchableSelect({
    options,
    value,
    onChange,
    label,
    placeholder = 'Selecione...',
    emptyLabel,
    error,
    fullWidth = false,
    disabled = false,
    required = false,
    className = '',
    id,
    dropdownVariant = 'overlay',
    searchPlaceholder = 'Buscar...',
}: SearchableSelectProps) {
    const generatedId = useId();
    const selectId = id ?? generatedId;

    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(0);

    const debouncedQuery = useDebounce(query, 250);

    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);

    const baseOptions: SearchableSelectOption[] =
        emptyLabel && !debouncedQuery
            ? [{ value: '', label: emptyLabel }, ...options]
            : options;
    const filteredOptions = filtrarOpcoes(baseOptions, debouncedQuery);
    const agrupado = options.some((o) => o.group);

    const selectedOption = options.find((o) => o.value === value);
    const selectedLabel = selectedOption?.label ?? '';

    const close = useCallback(() => {
        setIsOpen(false);
        setQuery('');
        setHighlightedIndex(0);
    }, []);

    const select = useCallback(
        (optionValue: string) => {
            onChange(optionValue);
            close();
        },
        [onChange, close],
    );

    useEffect(() => {
        function handleOutsideClick(e: MouseEvent) {
            if (
                containerRef.current &&
                !containerRef.current.contains(e.target as Node)
            ) {
                close();
            }
        }
        document.addEventListener('mousedown', handleOutsideClick);
        return () =>
            document.removeEventListener('mousedown', handleOutsideClick);
    }, [close]);

    useEffect(() => {
        if (!listRef.current) return;
        // Pelo indice da opcao, nao pela posicao na lista: os cabecalhos de
        // grupo tambem sao itens da lista.
        const item = listRef.current.querySelector<HTMLElement>(`[data-index="${highlightedIndex}"]`);
        item?.scrollIntoView({ block: 'nearest' });
    }, [highlightedIndex]);

    function handleToggle() {
        if (disabled) return;
        if (isOpen) {
            close();
        } else {
            setIsOpen(true);
            setTimeout(() => inputRef.current?.focus(), 0);
        }
    }

    function handleKeyDown(e: React.KeyboardEvent) {
        if (!isOpen) {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                e.preventDefault();
                setIsOpen(true);
                setTimeout(() => inputRef.current?.focus(), 0);
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex((i) =>
                    Math.min(i + 1, filteredOptions.length - 1),
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex((i) => Math.max(i - 1, 0));
                break;
            case 'Enter':
                e.preventDefault();
                if (filteredOptions[highlightedIndex]) {
                    select(filteredOptions[highlightedIndex].value);
                }
                break;
            case 'Escape':
                e.preventDefault();
                close();
                break;
            case 'Tab':
                close();
                break;
        }
    }

    const wrapperClasses = [
        styles.wrapper,
        error ? styles.hasError : '',
        fullWidth ? styles.fullWidth : '',
        disabled ? styles.disabled : '',
        className,
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div className={wrapperClasses} ref={containerRef}>
            {label && (
                <label htmlFor={selectId} className={styles.label}>
                    {label}
                    {required && <span className={styles.required}> *</span>}
                </label>
            )}

            <div
                id={selectId}
                role="combobox"
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-required={required}
                aria-disabled={disabled}
                aria-controls={`${selectId}-listbox`}
                tabIndex={disabled ? -1 : 0}
                className={`${styles.trigger} ${isOpen ? styles.triggerOpen : ''}`}
                onClick={handleToggle}
                onKeyDown={handleKeyDown}
            >
                {value ? (
                    <span className={styles.selectedLabel}>
                        {selectedOption?.prefix && (
                            <span className={styles.prefix}>{selectedOption.prefix}</span>
                        )}
                        {selectedLabel}
                    </span>
                ) : (
                    <span className={styles.placeholder}>
                        {emptyLabel || placeholder}
                    </span>
                )}
                <FiChevronDown
                    size={14}
                    className={`${styles.chevron} ${isOpen ? styles.arrowOpen : ''}`}
                />
            </div>

            {isOpen && (
                    <div
                        className={[
                            styles.dropdown,
                            dropdownVariant === 'inline' ? styles.dropdownInline : '',
                        ]
                            .filter(Boolean)
                            .join(' ')}
                    >
                    <div className={`${styles.searchWrapper} ${agrupado ? styles.searchWrapperComIcone : ''}`}>
                        {agrupado && <FiSearch size={14} className={styles.searchIcon} aria-hidden />}
                        <input
                            ref={inputRef}
                            type="text"
                            className={styles.searchInput}
                            value={query}
                            onChange={(e) => {
                                setHighlightedIndex(0);
                                setQuery(e.target.value);
                            }}
                            onKeyDown={handleKeyDown}
                            placeholder={searchPlaceholder}
                            aria-label="Buscar opção"
                            autoComplete="off"
                        />
                    </div>

                    <ul
                        ref={listRef}
                        id={`${selectId}-listbox`}
                        role="listbox"
                        className={styles.list}
                        aria-label={label}
                    >
                        {filteredOptions.length === 0 ? (
                            <li className={styles.empty}>Nenhuma opção encontrada</li>
                        ) : (
                            filteredOptions.map((opt, idx) => (
                                <Fragment key={opt.value === '' ? '__empty__' : opt.value}>
                                    {abreGrupo(filteredOptions, idx) && (
                                        <li role="presentation" className={styles.groupLabel}>
                                            {opt.group}
                                        </li>
                                    )}
                                    <li
                                        role="option"
                                        data-index={idx}
                                        aria-selected={opt.value === value}
                                        className={[
                                            styles.option,
                                            opt.group ? styles.optionInGroup : '',
                                            opt.value === value ? styles.optionSelected : '',
                                            idx === highlightedIndex
                                                ? styles.optionHighlighted
                                                : '',
                                        ]
                                            .filter(Boolean)
                                            .join(' ')}
                                        onMouseEnter={() => setHighlightedIndex(idx)}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            select(opt.value);
                                        }}
                                    >
                                        {opt.prefix && (
                                            <span className={styles.prefix}>{opt.prefix}</span>
                                        )}
                                        <span className={styles.optionLabel}>{opt.label}</span>
                                    </li>
                                </Fragment>
                            ))
                        )}
                    </ul>
                </div>
            )}

            {error && <span className={styles.errorMessage}>{error}</span>}

            <input type="hidden" name={selectId} value={value} required={required} />
        </div>
    );
}
